import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Images from "assets/Images";

// Isolated instance with no global interceptors — prevents 401 redirect on wrong password
const authAxios = axios.create();

import { apiBaseUrl } from '../../services/config';
const API = apiBaseUrl;

// Map API error messages to user-friendly text
const friendlyError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  const d = detail.toLowerCase();
  if (d.includes("deactivated"))
    return "Your account has been deactivated. Please contact your HR administrator.";
  if (d.includes("suspended"))
    return "Your company account is suspended. Please contact support@hirality.com.";
  if (d.includes("invalid email") || d.includes("invalid email or password"))
    return "Incorrect email or password. Please try again.";
  if (d.includes("not found"))
    return "No account found with this email address.";
  if (d.includes("expired"))
    return "Your session has expired. Please sign in again.";
  if (d.includes("connection") || d.includes("network"))
    return "Cannot connect to server. Please check your internet connection.";
  return detail;
};

export default function Login() {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [tab,           setTab]           = useState("login");
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [fEmail,        setFEmail]        = useState("");
  const [fMsg,          setFMsg]          = useState("");
  const [fLoading,      setFLoading]      = useState(false);
  const [resetToken,    setResetToken]    = useState("");
  const [newPassword,   setNewPassword]   = useState("");
  const [confirmPwd,    setConfirmPwd]    = useState("");
  const [resetLoading,  setResetLoading]  = useState(false);
  const [resetMsg,      setResetMsg]      = useState("");
  const [resetError,    setResetError]    = useState("");

  // On mount — check if URL has ?token=...&type=reset
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token  = params.get("token");
    const type   = params.get("type");
    if (token && type === "reset") {
      setResetToken(token);
      setTab("reset");
      window.history.replaceState({}, "", "/login");
    }
  }, [location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();   // prevent native form submit / page reload
    e.stopPropagation();
    if (loading) return;

    setLoading(true);

    try {
      const { data } = await authAxios.post(`${API}/auth/login`, { email, password });

      // ATS-only companies authenticate fine (auth is shared) but must not
      // be able to enter the HRMS UI — there's no HRMS frontend session for
      // them to land in. Block here rather than after navigating in, and
      // don't persist any session for it.
      if (data.user.role !== "super_admin" && data.user.hrms_enabled === false) {
        setError("HRMS access is not enabled for your company. Please contact your administrator.");
        setLoading(false);
        return;
      }

      setError("");

      // ── Only clear session AFTER successful login ──
      sessionStorage.clear();
      localStorage.removeItem("platform_token");
      localStorage.removeItem("platform_user");
      localStorage.removeItem("isLoggedIn");

      sessionStorage.setItem("platform_token", data.access_token);
      sessionStorage.setItem("platform_user", JSON.stringify({
        id:             data.user.id,
        email:          data.user.email,
        full_name:      `${data.user.first_name} ${data.user.last_name}`.trim(),
        first_name:     data.user.first_name,
        last_name:      data.user.last_name,
        role:           data.user.role,
        company_id:     data.user.company_id,
        company_name:   data.user.company_name,
        avatar_color:   data.user.avatar_color || "#00C8FF",
        must_change_pw: data.user.must_change_pw,
        plan:           data.user.plan || "HRMS + AI Bundle",
        hrms_enabled:   data.user.hrms_enabled,
        ats_enabled:    data.user.ats_enabled,
      }));
      sessionStorage.setItem("isLoggedIn", "true");
      if (data.user.plan) {
        sessionStorage.setItem("company_plan", data.user.plan);
      }

      const r = data.user.redirect;
      if (r)                                      navigate(r);
      else if (data.user.role === "super_admin")  navigate("/super-admin/companies");
      else if (data.user.role === "company_admin") navigate("/admin/hrms");
      else                                        navigate("/employee/dashboard");

    } catch (err) {
      const detail = err.response?.data?.detail || "";
      setError(friendlyError(detail));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fLoading) return;
    setFLoading(true); setFMsg("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: fEmail });
      setFMsg("✅ If this email exists, a reset link has been sent.");
    } catch {
      // Always show same message for security (don't reveal if email exists)
      setFMsg("✅ If this email exists, a reset link has been sent.");
    } finally { setFLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setResetError(""); setResetMsg("");
    if (newPassword !== confirmPwd) {
      setResetError("Passwords do not match."); return;
    }
    if (newPassword.length < 8) {
      setResetError("Password must be at least 8 characters."); return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token:        resetToken,
        new_password: newPassword,
      });
      setResetMsg("✅ Password reset successfully! Redirecting to login…");
      setTimeout(() => {
        setTab("login");
        setNewPassword(""); setConfirmPwd("");
        setResetToken(""); setResetMsg("");
      }, 2500);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      setResetError(
        detail.includes("expired") ? "This reset link has expired. Please request a new one." :
        detail.includes("invalid") ? "Invalid reset link. Please request a new one." :
        detail || "Reset failed. Please try again."
      );
    } finally { setResetLoading(false); }
  };

  const S = {
    page: { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#06101F", fontFamily:"'Outfit',sans-serif" },
    card: { width:420, background:"#0D1B2E", border:"1px solid rgba(0,200,255,0.15)", borderRadius:16, padding:"40px 36px" },
    lbl:  { fontSize:12, color:"#7A93B4", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.08em" },
    inp:  { width:"100%", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(0,200,255,0.12)", borderRadius:9, color:"#E8F0FE", fontSize:14, padding:"11px 14px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box", marginBottom:16 },
    btn:  { width:"100%", background:"linear-gradient(135deg,#00C8FF,#7C5CFF)", border:"none", borderRadius:9, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:15, fontWeight:700, padding:"13px", cursor:"pointer" },
    link: { background:"none", border:"none", color:"#00C8FF", fontSize:13, cursor:"pointer", fontFamily:"Outfit,sans-serif", textDecoration:"underline", padding:0 },
  };

  const tabLabel = tab === "login"  ? "Sign in to your account"
                 : tab === "forgot" ? "Reset your password"
                 : "Set new password";

  const logoSrc = Images?.LOGO || Images?.logo || Images?.hirality || Images?.brandLogo;

  // Error type — deactivated accounts get a different style
  const isDeactivated = error.toLowerCase().includes("deactivated") ||
                        error.toLowerCase().includes("suspended");

  return (
    <div style={S.page}>
      <div style={S.card}>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ width:64, height:64, borderRadius:16,
            background:"linear-gradient(135deg,#00C8FF18,#7C5CFF18)",
            border:"1px solid rgba(0,200,255,0.15)",
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 14px", overflow:"hidden" }}>
            {logoSrc
              ? <img src={logoSrc} alt="Hirality"
                  style={{ width:48, height:48, objectFit:"contain" }}
                  onError={e => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextSibling.style.display = "flex";
                  }}/>
              : null}
            <div style={{ display: logoSrc ? "none" : "flex",
              alignItems:"center", justifyContent:"center",
              width:"100%", height:"100%",
              fontSize:26, fontWeight:800, color:"#00C8FF" }}>H</div>
          </div>
          <div style={{ fontSize:22, fontWeight:800, color:"#E8F0FE" }}>Hirality</div>
          <div style={{ fontSize:13, color:"#7A93B4", marginTop:4 }}>{tabLabel}</div>
        </div>

        {/* ── LOGIN ── */}
        {tab === "login" && (
          <form onSubmit={handleLogin} noValidate>
            <label style={S.lbl}>Email Address</label>
            <input style={S.inp} type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com" required autoComplete="email"
              onFocus={e => e.target.style.borderColor="rgba(0,200,255,0.4)"}
              onBlur={e  => e.target.style.borderColor="rgba(0,200,255,0.12)"}/>

            <label style={S.lbl}>Password</label>
            <input style={{ ...S.inp, marginBottom:8 }} type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" required autoComplete="current-password"
              onFocus={e => e.target.style.borderColor="rgba(0,200,255,0.4)"}
              onBlur={e  => e.target.style.borderColor="rgba(0,200,255,0.12)"}/>

            <div style={{ textAlign:"right", marginBottom:20 }}>
              <button type="button" style={S.link}
                onClick={() => { setTab("forgot"); setError(""); }}>
                Forgot password?
              </button>
            </div>

            {error && (
              <div style={{ marginBottom:16, padding:"12px 14px", borderRadius:8,
                background: isDeactivated ? "rgba(255,184,0,0.08)" : "rgba(255,77,109,0.08)",
                border: `1px solid ${isDeactivated ? "rgba(255,184,0,0.25)" : "rgba(255,77,109,0.25)"}`,
                color: isDeactivated ? "#FFB800" : "#FF4D6D",
                fontSize:13, lineHeight:1.6,
                display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10 }}>
                <div>
                  {isDeactivated ? "⚠️ " : "❌ "}{error}
                  {isDeactivated && (
                    <div style={{ marginTop:6, fontSize:12, color:"#7A93B4" }}>
                      Contact <a href="mailto:support@hirality.com"
                        style={{ color:"#00C8FF", textDecoration:"none" }}>
                        support@hirality.com
                      </a> to reactivate.
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => setError("")}
                  style={{ background:"none", border:"none", cursor:"pointer",
                    color: isDeactivated ? "#FFB800" : "#FF4D6D",
                    fontSize:16, lineHeight:1, padding:0, flexShrink:0, opacity:0.7 }}>
                  ✕
                </button>
              </div>
            )}

            <button style={{ ...S.btn, opacity:loading ? 0.7 : 1 }}
              type="submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        )}

        {/* ── FORGOT PASSWORD ── */}
        {tab === "forgot" && (
          <form onSubmit={handleForgot} noValidate>
            <div style={{ padding:"12px 14px", borderRadius:9,
              background:"rgba(0,200,255,0.06)",
              border:"1px solid rgba(0,200,255,0.12)",
              fontSize:13, color:"#7A93B4", marginBottom:20, lineHeight:1.6 }}>
              Enter your registered email. We'll send a reset link if the account exists.
            </div>

            <label style={S.lbl}>Email Address</label>
            <input style={{ ...S.inp, marginBottom:20 }} type="email" value={fEmail}
              onChange={e => setFEmail(e.target.value)}
              placeholder="your@email.com" required autoComplete="email"
              onFocus={e => e.target.style.borderColor="rgba(0,200,255,0.4)"}
              onBlur={e  => e.target.style.borderColor="rgba(0,200,255,0.12)"}/>

            {fMsg && (
              <div style={{ marginBottom:16, padding:"12px 14px", borderRadius:8,
                background:"rgba(0,229,163,0.08)", border:"1px solid rgba(0,229,163,0.2)",
                color:"#00E5A3", fontSize:13 }}>
                {fMsg}
              </div>
            )}

            <button style={{ ...S.btn, opacity:fLoading ? 0.7 : 1 }}
              type="submit" disabled={fLoading}>
              {fLoading ? "Sending…" : "Send Reset Link"}
            </button>

            <div style={{ textAlign:"center", marginTop:16 }}>
              <button type="button" style={S.link}
                onClick={() => { setTab("login"); setFMsg(""); }}>
                ← Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* ── RESET PASSWORD ── */}
        {tab === "reset" && (
          <form onSubmit={handleReset} noValidate>
            <div style={{ padding:"12px 14px", borderRadius:9,
              background:"rgba(0,200,255,0.06)",
              border:"1px solid rgba(0,200,255,0.12)",
              fontSize:13, color:"#7A93B4", marginBottom:20, lineHeight:1.6 }}>
              Enter your new password below.
            </div>

            <label style={S.lbl}>New Password</label>
            <input style={S.inp} type="password" value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Min. 8 characters" required autoComplete="new-password"
              onFocus={e => e.target.style.borderColor="rgba(0,200,255,0.4)"}
              onBlur={e  => e.target.style.borderColor="rgba(0,200,255,0.12)"}/>

            <label style={S.lbl}>Confirm New Password</label>
            <input style={{ ...S.inp, marginBottom:20 }} type="password" value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              placeholder="Repeat password" required autoComplete="new-password"
              onFocus={e => e.target.style.borderColor="rgba(0,200,255,0.4)"}
              onBlur={e  => e.target.style.borderColor="rgba(0,200,255,0.12)"}/>

            {resetError && (
              <div style={{ marginBottom:16, padding:"12px 14px", borderRadius:8,
                background:"rgba(255,77,109,0.08)", border:"1px solid rgba(255,77,109,0.2)",
                color:"#FF4D6D", fontSize:13 }}>
                ❌ {resetError}
              </div>
            )}
            {resetMsg && (
              <div style={{ marginBottom:16, padding:"12px 14px", borderRadius:8,
                background:"rgba(0,229,163,0.08)", border:"1px solid rgba(0,229,163,0.2)",
                color:"#00E5A3", fontSize:13 }}>
                {resetMsg}
              </div>
            )}

            <button style={{ ...S.btn, opacity:resetLoading ? 0.7 : 1 }}
              type="submit" disabled={resetLoading}>
              {resetLoading ? "Resetting…" : "Reset Password"}
            </button>
          </form>
        )}

        <div style={{ marginTop:16, textAlign:"center", fontSize:11.5, color:"#3D5473" }}>
          Each browser tab maintains its own session
        </div>
      </div>
    </div>
  );
}